/** Sender configuration belongs to the gateway row: where its IDs come from (a choice), then that source's fields. */
import { useState, type FC } from 'react';
import { senderGroupsForLane, senderSettingsForLane, type DropGenLaneSenders } from '@trafficpulse/shared';
import { Sparkles, X } from 'lucide-react';
import type { DraftPoolRow, DraftSenderDefaults } from '../../../../drop-generator.types';
import type { GatewayOp } from '../../state/gateway-draft';
import { GenerateLaneSenders } from './GenerateLaneSenders';
import { SenderAiFields } from './SenderAiFields';
import { SenderIdInput } from './SenderIdInput';
import { SenderSourceChoice } from './source/SenderSourceChoice';

interface Props {
  countryKey?: string;
  lane: DraftPoolRow;
  defaults: DraftSenderDefaults;
  onOp: (op: GatewayOp) => void;
}

export const LaneSenders: FC<Props> = ({ lane, defaults, onOp, countryKey }) => {
  const [generateOpen, setGenerateOpen] = useState(false);
  const set = (patch: Partial<DropGenLaneSenders>) => onOp({ kind: 'laneSenders', id: lane.id, patch });
  const ids = lane.senders.ids;
  const selected = senderGroupsForLane(defaults, lane);
  const settings = senderSettingsForLane({ amount: defaults.amount, mode: defaults.mode === 'UNIQUE' ? 'UNIQUE' : 'SHARED' }, lane);
  const automatic = !lane.senders.staticOnly && (ids.length === 0 || (lane.senders.aiMode ?? defaults.aiMode) === 'ALWAYS');
  const showGenerator = automatic || generateOpen;
  const savedOpen = !automatic || ids.length > 0;
  return (
    <div className="space-y-4 border-t border-border/50 p-4" data-testid="lane-senders">
      <SenderSourceChoice automatic={automatic} ids={ids} amount={settings.amount} mode={settings.mode} groups={selected.length}
        onChange={(value) => { set({ staticOnly: !value, ...(value ? { aiMode: 'ALWAYS' } : {}) }); setGenerateOpen(false); }} />
      <div className="space-y-4">
        {savedOpen ? <SavedIds ids={ids} automatic={automatic} generateOpen={generateOpen} onSet={set} onToggle={() => setGenerateOpen((v) => !v)} /> : null}
        {showGenerator ? <section aria-label="AI sender settings" className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
          <SenderAiFields groupIds={selected} amount={settings.amount} mode={automatic ? settings.mode : undefined}
            onGroups={(groups) => set({ aiGroupIds: groups, aiGroupId: groups[0] ?? null })}
            onAmount={(amount) => set({ amount })} onMode={(mode) => set({ mode })} />
          <GenerateLaneSenders countryKey={countryKey} groupIds={selected} amount={settings.amount} existing={ids} preview={automatic}
            onApply={(names) => { set({ ids: names, staticOnly: true }); setGenerateOpen(false); }} />
        </section> : null}
      </div>
    </div>
  );
};

const SavedIds: FC<{ ids: string[]; automatic: boolean; generateOpen: boolean; onSet: (patch: Partial<DropGenLaneSenders>) => void; onToggle: () => void }> = ({ ids, automatic, generateOpen, onSet, onToggle }) => (
  <div className="space-y-3">
    {automatic ? <p className="text-xs text-muted-foreground">Your saved IDs — they go out with the AI names.</p> : null}
    {ids.length > 0 ? <div className="flex flex-wrap gap-1.5">
      {ids.map((name) => <span key={name} className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 py-1 pl-2.5 pr-1 font-mono text-xs">
        {name}
        <button type="button" aria-label={`Remove sender ID ${name}`} onClick={() => onSet({ ids: ids.filter((x) => x !== name) })} className="rounded p-0.5 text-muted-foreground hover:text-destructive">
          <X className="h-3 w-3" />
        </button>
      </span>)}
    </div> : null}
    <SenderIdInput existing={ids} onAdd={(name) => onSet({ ids: [...ids, name] })} />
    {!automatic ? <button type="button" aria-expanded={generateOpen} onClick={onToggle} className="inline-flex items-center gap-1.5 text-xs font-medium text-primary">
      <Sparkles className="h-3.5 w-3.5" />{generateOpen ? 'Hide generator' : 'Generate with AI'}
    </button> : null}
  </div>
);
